import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import type { CountryCode } from 'libphonenumber-js';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useIntl } from 'react-intl';

import * as api from '../api/loginApi';
import type { PhoneSetupStep } from '../types/login';

export const usePhoneSetup = (onComplete: () => void) => {
    const intl = useIntl();
    const [step, setStep] = useState<PhoneSetupStep>('enter-number');
    const [phoneCountry, setPhoneCountry] = useState<CountryCode>('US');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    // -----------------------------------------------------------------------
    // Pre-load user country
    // -----------------------------------------------------------------------

    useQuery({
        queryKey: ['userCountry'],
        queryFn: api.getUserCountry,
        select: (data) => {
            if (data.success && data.countryIsoCode) {
                setPhoneCountry(data.countryIsoCode as CountryCode);
            }
            return data;
        },
    });

    // -----------------------------------------------------------------------
    // Register phone number (AddPhoneNumber)
    // -----------------------------------------------------------------------

    const registerMutation = useMutation({
        mutationFn: (number: string) => api.registerPhone(number),
        onSuccess: (data) => {
            if (data.success) {
                setStep('verify-code');
            } else {
                setErrorMessage(
                    intl.formatMessage({
                        id: 'login.phoneSetup.error.sendFailed',
                        defaultMessage: 'Unable to send verification code',
                    }),
                );
            }
        },
    });

    // -----------------------------------------------------------------------
    // Verify phone code (VerifyPhoneNumber)
    // -----------------------------------------------------------------------

    const verifyMutation = useMutation({
        mutationFn: (code: string) => api.verifyPhone({ code, phoneNumber }),
        onSuccess: (data) => {
            if (data.success) {
                setStep('success');
            } else {
                setErrorMessage(
                    intl.formatMessage({
                        id: 'login.phoneSetup.error.verifyFailed',
                        defaultMessage: 'Verification failed',
                    }),
                );
            }
        },
    });

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    const submitPhoneNumber = () => {
        setErrorMessage(undefined);
        try {
            const parsed = parsePhoneNumber(phoneNumber, phoneCountry);
            if (!parsed || !parsed.isValid()) {
                setErrorMessage(
                    intl.formatMessage({
                        id: 'login.phoneSetup.error.invalidNumber',
                        defaultMessage: 'Please enter a valid phone number',
                    }),
                );
                return;
            }
            registerMutation.mutate(parsed.number);
        } catch {
            setErrorMessage(
                intl.formatMessage({
                    id: 'login.phoneSetup.error.invalidNumber',
                    defaultMessage: 'Please enter a valid phone number',
                }),
            );
        }
    };

    const submitVerificationCode = (code: string) => {
        setErrorMessage(undefined);
        verifyMutation.mutate(code);
    };

    const changeNumber = () => {
        setStep('enter-number');
        setErrorMessage(undefined);
    };

    const finish = () => {
        onComplete();
    };

    const setPhoneCountryFromString = (code: string) => {
        setPhoneCountry(code as CountryCode);
    };

    return {
        step,
        phoneCountry,
        phoneNumber,
        isRegistering: registerMutation.isPending,
        isVerifying: verifyMutation.isPending,
        registerError: errorMessage ?? registerMutation.error?.message,
        verifyError: errorMessage ?? verifyMutation.error?.message,
        setPhoneCountry: setPhoneCountryFromString,
        setPhoneNumber,
        submitPhoneNumber,
        submitVerificationCode,
        changeNumber,
        finish,
    };
};
